import React, { useState, useMemo, useRef } from 'react';
import { Redirect } from 'react-router-dom';
import { withStyles, Tooltip, IconButton, Typography } from '@material-ui/core';
import { FindInPage } from '@material-ui/icons';
import { connect } from 'react-redux';

import ReportDataList from './ReportDataList';
import { hasPermission, createHistory, hasAtLeastOnePermission } from '../../sc-utilities';


const styles = theme => ({
    root: {
        width: `100%`,
        height: 'auto',
        paddingLeft: theme.spacing(-1),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    }
});

const ReportData = props => {
    const tableRef = useRef();
    const { classes, translations, columns, permissions } = props;

    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, reportDataID: null, redirectPath: '' });
    const redirectHandler = (path, reportDataID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, reportDataID: reportDataID, redirectPath: path })
    };

    let visible = columns && columns['ReportData'];

    const tableColumns = useMemo(() => {
        return [{
            title: translations["action"] || "Action", field: "Action",
            hidden: visible ? visible["Action"] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            filtering: false,
            selection: true,
            render: rowData => <>
                {
                    hasPermission(permissions, 'report_data_load') ?
                        <Tooltip title={translations["report_data_details"] || "Report data details"}>
                            <IconButton
                                id={`edit_report_data${rowData.ReportDataID}`}
                                value={rowData.ReportDataID}
                                aria-label={translations["report_data_details"] || "Report data details"}
                                onClick={() => redirectHandler(`/settings/report-data/edit?reportDataID=${rowData.ReportDataID}`)}>
                                <FindInPage />
                            </IconButton>
                        </Tooltip> : null
                }
            </>
        },
        {
            title: translations["name"] || "Name", field: "Name",
            hidden: visible ? visible["Name"] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={"caption"}>{rowData.Name ? rowData.Name : translations['short_not_assigned'] || 'n/a'}</Typography>
            }
        },
        {
            title: translations["company_name"] || "Company name", field: "CompanyName",
            hidden: visible ? visible["CompanyName"] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={"caption"}>{rowData.CompanyName ? rowData.CompanyName : translations['short_not_assigned'] || 'n/a'}</Typography>
            }
        },
        {
            title: translations["address"] || "Address", field: "Address",
            hidden: visible ? visible["Address"] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={"caption"}>{rowData.Address ? rowData.Address : translations['short_not_assigned'] || 'n/a'}</Typography>
            }
        },
        {
            title: translations["include_performed_diagnose_cases_only"] || 'Include performed diagnose cases only', field: 'IncludePerformedDiagnoseCasesOnly',
            hidden: visible ? visible['IncludePerformedDiagnoseCasesOnly'] : false,
            filtering: true,
            lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
            defaultFilter: ['0', '1'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.IncludePerformedDiagnoseCasesOnly ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
            }
        },
        {
            title: translations["add_photos_to_erasure_reports"] || 'Add photos to erasure reports', field: 'AddPhotosToErasureReports',
            hidden: visible ? visible['AddPhotosToErasureReports'] : false,
            filtering: true,
            lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
            defaultFilter: ['0', '1'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.AddPhotosToErasureReports ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
            }
        },
        {
            title: translations["erasure_report_photos_layout"] || 'Erasure report photos layout', field: 'ErasureReportPhotosLayout',
            hidden: visible ? visible['ErasureReportPhotosLayout'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={"caption"}>{rowData.ErasureReportPhotosLayout ? rowData.ErasureReportPhotosLayout : translations['short_not_assigned'] || 'n/a'}</Typography>
            }
        },
        {
            title: translations["add_photos_to_diagnose_reports"] || 'Add photos to diagnose reports', field: 'AddPhotosToDiagnoseReports',
            hidden: visible ? visible['AddPhotosToDiagnoseReports'] : false,
            filtering: true,
            lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
            defaultFilter: ['0', '1'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.AddPhotosToDiagnoseReports ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
            }
        },
        {
            title: translations["diagnose_report_photos_layout"] || 'Diagnose report photos layout', field: 'DiagnoseReportPhotosLayout',
            hidden: visible ? visible['DiagnoseReportPhotosLayout'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={"caption"}>{rowData.DiagnoseReportPhotosLayout ? rowData.DiagnoseReportPhotosLayout : translations['short_not_assigned'] || 'n/a'}</Typography>
            }
        },
        {
            title: translations["add_photos_to_verification_reports"] || 'Add photos to verification reports', field: 'AddPhotosToVerificationReports',
            hidden: visible ? visible['AddPhotosToVerificationReports'] : false,
            filtering: true,
            lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
            defaultFilter: ['0', '1'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.AddPhotosToVerificationReports ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
            }
        },
        {
            title: translations["verification_report_photos_layout"] || 'Verification report photos layout', field: 'VerificationReportPhotosLayout',
            hidden: visible ? visible['VerificationReportPhotosLayout'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={"caption"}>{rowData.VerificationReportPhotosLayout ? rowData.VerificationReportPhotosLayout : translations['short_not_assigned'] || 'n/a'}</Typography>
            }
        },
        {
            title: translations["include_grading_in_erasure_reports"] || 'Include grading info in erasure reports', field: 'IncludeGradingInErasureReports',
            hidden: visible ? visible['IncludeGradingInErasureReports'] : false,
            filtering: true,
            lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
            defaultFilter: ['0', '1'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.IncludeGradingInErasureReports ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
            }
        },
        {
            title: translations["include_diagnose_in_erasure_reports"] || 'Include diagnose info in erasure reports', field: 'IncludeDiagnoseInErasureReports',
            hidden: visible ? visible['IncludeDiagnoseInErasureReports'] : false,
            filtering: true,
            lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
            defaultFilter: ['0', '1'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.IncludeDiagnoseInErasureReports ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
            }
        },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes, translations, visible])

    if (!hasAtLeastOnePermission(permissions, ['report_data_load'])) {
        tableColumns.shift();
    }

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {
                hasPermission(permissions, 'report_data_list') ?
                    <ReportDataList
                        tableColumns={tableColumns}
                        permissions={permissions}
                        redirectHandler={redirectHandler}
                        tableRef={tableRef}
                    /> : null
            }
        </div>
    );
}

const mapStateToProps = state => {
    return {
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
};

export default connect(
    mapStateToProps,
    null
)(withStyles(styles)(ReportData));
